import { HttpContext } from '@angular/common/http';
import { computed, effect, inject, Injectable, Signal, signal, untracked } from '@angular/core';
import { AccountModel, AuthProvider, Role } from '@gs/shared';
import { SystemService } from '@gs/shared/services';
import { SKIP_ERROR_HANDLING } from '../interceptors/auth/auth.interceptor';

/** Rights that allow designing pages, same gate as the CS3 AdminHeader and the Designer endpoints */
const EDITOR_RIGHTS: string[] = ['pages.design', 'pages.design.advanced'];
/** Backend roles allowed to open the Designer (same as its AuthGuard), keeps shop customers out */
const EDITOR_ROLES: Role[] = ['admin', 'user'];

/**
 * Roles and rights of the logged-in user from the account info, loaded once per login.
 * Visitors without an auth cookie never trigger a request.
 */
@Injectable({
  providedIn: 'root'
})
export class RightsService {
  // Services
  private readonly authProvider = inject(AuthProvider);
  private readonly systemService = inject(SystemService);

  // Properties & Signals
  /** Whether the logged-in user is a backend user holding page design rights (editor toolbar) */
  readonly isEditor: Signal<boolean> = computed(() => this.hasRole(...EDITOR_ROLES) && this.hasRight(...EDITOR_RIGHTS));
  /** Roles of the logged-in user in lower case, empty for visitors */
  private readonly roles = signal<string[]>([]);
  /** Rights of the logged-in user in lower case, empty for visitors */
  private readonly rights = signal<string[]>([]);

  constructor() {
    effect(() => {
      // Loads roles and rights on every login and drops them again on logout
      const isLoggedIn = this.authProvider.isLoggedIn();
      untracked(() => (isLoggedIn ? this.load() : this.reset()));
    });
    if (this.systemService.browser) this.authProvider.checkAuth();
  }

  // Methods
  /**
   * Checks whether the logged-in user holds one of the given roles, reactive inside `computed()`.
   * @param roles The accepted roles
   * @returns True if the user holds at least one of them
   */
  hasRole(...roles: Role[]): boolean {
    return this.roles().some((role: string) => roles.includes(role as Role));
  }

  /**
   * Checks whether the logged-in user holds one of the given rights, reactive inside `computed()`.
   * @param rights The accepted rights, e.g. `pages.design`
   * @returns True if the user holds at least one of them
   */
  hasRight(...rights: string[]): boolean {
    return this.rights().some((right: string) => rights.includes(right));
  }

  /**
   * Loads the account info silently, a failure leaves the user without roles and rights.
   */
  private load(): void {
    if (!this.systemService.browser || !this.authProvider.cookies().userAuth) return;
    this.authProvider.info(new HttpContext().set(SKIP_ERROR_HANDLING, true)).subscribe({
      next: (account: AccountModel) => {
        this.roles.set(account.roles?.map((role: string) => role.toLowerCase()) ?? []);
        this.rights.set(account.rights?.map((right: string) => right.toLowerCase()) ?? []);
      },
      error: () => this.reset()
    });
  }

  /**
   * Drops all roles and rights, e.g. on logout.
   */
  private reset(): void {
    this.roles.set([]);
    this.rights.set([]);
  }
}
